import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown } from "@phosphor-icons/react";
import { STOCK } from "@/data";
import { useBooking } from "@/context/booking";

const lineParent = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } } };
const lineChild = { hidden: { y: "110%" }, show: { y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } } };

const MaskLine = ({ children, className }) => (
  <span className="block overflow-hidden">
    <motion.span variants={lineChild} className={`block ${className}`}>{children}</motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const { openBooking } = useBooking();

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={STOCK.hero} alt="Luxury Epic Memories event" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
      <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[80vw] h-[60vh] purple-glow" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-5 md:px-8 flex flex-col justify-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-[#D4AF37] uppercase tracking-[0.35em] text-xs md:text-sm font-semibold mb-5">
          Not Just Creating Memories… Creating Epic Memories.
        </motion.p>

        <motion.h1 variants={lineParent} initial="hidden" animate="show"
          className="font-display font-medium text-white leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] max-w-5xl">
          <MaskLine>Where Every</MaskLine>
          <MaskLine className="italic gold-text">Celebration</MaskLine>
          <MaskLine>Becomes an Epic Memory</MaskLine>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
          className="mt-8 max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed">
          Unforgettable experiences through themed parties, corporate events, weddings,
          game nights, paint parties, fundraisers, fashion shows & community events.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35 }}
          className="mt-10 flex flex-col sm:flex-row gap-4">
          <button data-testid="hero-book-btn" onClick={() => openBooking()}
            className="bg-[#D4AF37] text-black font-heading font-bold rounded-full px-8 py-4 hover:bg-[#F3E5AB] transition-colors text-base">
            Book Your Event
          </button>
          <button data-testid="hero-tickets-btn" onClick={() => scrollTo("events")}
            className="border border-white/30 text-white font-heading font-bold rounded-full px-8 py-4 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-base">
            Buy Event Tickets
          </button>
        </motion.div>
      </div>

      <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        onClick={() => scrollTo("about")} data-testid="hero-scroll-cue"
        className="absolute bottom-28 left-1/2 -translate-x-1/2 z-10 text-white/60 hover:text-[#D4AF37] transition-colors">
        <ArrowDown size={26} className="animate-bounce" />
      </motion.button>

      <div className="absolute bottom-0 inset-x-0 z-10 border-y border-white/10 bg-black/40 backdrop-blur-sm py-4">
        <Marquee speed={45} gradient={false} autoFill>
          {Array(6).fill(0).map((_, i) => (
            <span key={i} className="font-display italic text-2xl md:text-3xl text-zinc-500 mx-8">
              Creating Epic Memories <span className="gold-text not-italic mx-8">✦</span>
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
};
